"use client";

import { useCallback, useState } from "react";
import Image from "next/image";
import Turnstile from "@/components/shared/Turnstile";

export type Tier = "private" | "corporate";

type Props = {
  locale: "fr" | "en";
  initialTier: Tier;
  backgroundSrc: string;
  t: {
    eyebrow: string;
    title: string;
    subtitle: string;
    tierLabel: string;
    tierPrivate: string;
    tierCorporate: string;
    interestsLabel: string;
    services: string[];
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    message: string;
    submit: string;
    successTitle: string;
    successText: string;
    /** Optionnels (repli sur le texte d'origine selon la langue) */
    errorText?: string;
    sending?: string;
  };
};

export default function BecomeMemberForm({
  locale,
  initialTier,
  backgroundSrc,
  t,
}: Props) {
  const [tier, setTier] = useState<Tier>(initialTier);
  const [interests, setInterests] = useState<string[]>([]);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    message: "",
  });

  const toggleInterest = (label: string) =>
    setInterests((prev) =>
      prev.includes(label) ? prev.filter((s) => s !== label) : [...prev, label]
    );
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");

  const onToken = useCallback((token: string) => setTurnstileToken(token), []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(false);
    try {
      const res = await fetch("/api/membership", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          tier,
          interests,
          locale,
          source: "membership_join",
          turnstileToken,
        }),
      });
      if (!res.ok) throw new Error("request_failed");
      setSubmitted(true);
    } catch {
      setError(true);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full bg-transparent border-0 border-b border-white/25 pb-2 text-sm text-white placeholder-transparent focus:outline-none focus:border-white/60 transition-colors duration-200 font-body";
  const fieldClass = "flex flex-col gap-1.5";
  const labelClass = "text-[10px] tracking-[0.18em] uppercase text-white/45 font-body";

  const tierBtn = (active: boolean) =>
    [
      "rounded-full border px-6 py-3 text-[11px] tracking-[0.16em] uppercase font-body transition-colors duration-200 cursor-pointer",
      active
        ? "border-white bg-white text-black"
        : "border-white/25 text-white/70 hover:border-white/50 hover:text-white",
    ].join(" ");

  return (
    <section className="relative flex min-h-screen items-center overflow-hidden">
      {/* Background */}
      <Image
        src={backgroundSrc}
        alt="Welconcept"
        fill
        className="object-cover object-center"
        sizes="100vw"
        priority
      />
      <div className="absolute inset-0 bg-black/75" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/50" />

      {/* Content */}
      <div className="relative z-10 mx-auto w-full max-w-3xl px-4 sm:px-6 lg:px-8 py-28">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-[10px] tracking-[0.32em] uppercase text-white/45 font-body">
            {t.eyebrow}
          </p>
          <h1 className="mt-6 font-heading text-5xl md:text-6xl text-white font-light italic leading-tight">
            {t.title}
          </h1>
          <p className="mt-5 text-sm text-white/55 font-body max-w-md mx-auto leading-relaxed">
            {t.subtitle}
          </p>
        </div>

        {submitted ? (
          /* Success state */
          <div className="flex flex-col items-center gap-4 py-12 text-center">
            <div className="w-10 h-px bg-white/40" />
            <p className="font-heading text-3xl text-white font-light italic">
              {t.successTitle}
            </p>
            <p className="text-sm text-white/50 font-body leading-relaxed max-w-sm">
              {t.successText}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-8">
            {/* Tier selector */}
            <div className="flex flex-col items-center gap-3">
              <span className={labelClass}>{t.tierLabel}</span>
              <div className="flex flex-wrap justify-center gap-3">
                <button
                  type="button"
                  onClick={() => setTier("private")}
                  className={tierBtn(tier === "private")}
                  aria-pressed={tier === "private"}
                >
                  {t.tierPrivate}
                </button>
                <button
                  type="button"
                  onClick={() => setTier("corporate")}
                  className={tierBtn(tier === "corporate")}
                  aria-pressed={tier === "corporate"}
                >
                  {t.tierCorporate}
                </button>
              </div>
            </div>

            {/* Services d'intérêt — multi-sélection facultative */}
            <div className="flex flex-col items-center gap-3">
              <span className={labelClass}>{t.interestsLabel}</span>
              <div className="flex flex-wrap justify-center gap-2">
                {t.services.map((service) => {
                  const selected = interests.includes(service);
                  return (
                    <button
                      key={service}
                      type="button"
                      onClick={() => toggleInterest(service)}
                      aria-pressed={selected}
                      className={[
                        "inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-[10px] tracking-[0.12em] uppercase font-body transition-colors duration-200 cursor-pointer",
                        selected
                          ? "border-white/70 bg-white/10 text-white"
                          : "border-white/15 text-white/50 hover:border-white/40 hover:text-white/80",
                      ].join(" ")}
                    >
                      <span
                        className={[
                          "text-[11px] leading-none transition-transform duration-200",
                          selected ? "rotate-45" : "",
                        ].join(" ")}
                        aria-hidden
                      >
                        +
                      </span>
                      {service}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Row 1 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className={fieldClass}>
                <label className={labelClass}>{t.firstName}</label>
                <input
                  name="firstName"
                  type="text"
                  required
                  value={form.firstName}
                  onChange={handleChange}
                  className={inputClass}
                />
              </div>
              <div className={fieldClass}>
                <label className={labelClass}>{t.lastName}</label>
                <input
                  name="lastName"
                  type="text"
                  required
                  value={form.lastName}
                  onChange={handleChange}
                  className={inputClass}
                />
              </div>
            </div>

            {/* Row 2 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className={fieldClass}>
                <label className={labelClass}>{t.phone}</label>
                <input
                  name="phone"
                  type="tel"
                  required
                  value={form.phone}
                  onChange={handleChange}
                  className={inputClass}
                />
              </div>
              <div className={fieldClass}>
                <label className={labelClass}>{t.email}</label>
                <input
                  name="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  className={inputClass}
                />
              </div>
            </div>

            {/* Message */}
            <div className={fieldClass}>
              <label className={labelClass}>{t.message}</label>
              <input
                name="message"
                type="text"
                value={form.message}
                onChange={handleChange}
                className={inputClass}
              />
            </div>

            {/* Turnstile (rendu uniquement si la clé publique est définie) */}
            <Turnstile onToken={onToken} />

            {error && (
              <p className="text-center text-xs text-red-300/80 font-body">
                {t.errorText ??
                  (locale === "fr"
                    ? "Une erreur est survenue. Merci de réessayer."
                    : "Something went wrong. Please try again.")}
              </p>
            )}

            {/* Submit */}
            <div className="flex justify-center mt-2">
              <button
                type="submit"
                disabled={submitting}
                className="rounded-full bg-white text-black text-xs tracking-[0.2em] uppercase font-body font-medium px-14 py-3.5 hover:bg-white/90 transition-colors duration-200 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting
                  ? (t.sending ??
                    (locale === "fr" ? "Envoi…" : "Sending…"))
                  : t.submit}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
